"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.nativeMerge = exports.nativeMergeResultSchema = exports.nativeMergeArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Merge operation types
var mergeOperationSchema = zod_1.z.enum([
    'array_concat', // Simple array concatenation
    'array_union', // Array concatenation with deduplication
    'object_merge', // Object merging with conflict resolution
    'csv_merge', // CSV data merging with column alignment
]);
// Conflict resolution strategies for object merging
var conflictResolutionSchema = zod_1.z.enum([
    'first_wins', // Keep value from first object
    'last_wins', // Keep value from last object (default)
    'merge_arrays', // If both values are arrays, concatenate them
    'error_on_conflict', // Throw error if conflicting values found
]);
// Input schema for merge operations
exports.nativeMergeArgsSchema = zod_1.z.object({
    operation: mergeOperationSchema.describe('Type of merge operation to perform:\n' +
        '• array_concat: Simple concatenation of all input arrays\n' +
        '• array_union: Concatenate arrays and remove duplicates\n' +
        '• object_merge: Merge objects with conflict resolution\n' +
        '• csv_merge: Merge structured data with column alignment'),
    inputs: zod_1.z
        .array(zod_1.z.any().nullable())
        .min(1)
        .describe('Array of inputs to merge. Type depends on operation:\n' +
        '• array_concat/array_union: Array of arrays to concatenate\n' +
        '• object_merge: Array of objects to merge\n' +
        '• csv_merge: Array of CSV data arrays (with headers)\n' +
        '• null values are filtered out; if only one non-null input remains, it is returned directly'),
    conflictResolution: conflictResolutionSchema
        .default('last_wins')
        .describe('How to handle conflicting values in object_merge operation'),
    deduplicateBy: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('For array_union: field path to deduplicate by (e.g., "id", "email"). If not specified, deduplicates by entire value'),
    preserveOrder: zod_1.z
        .boolean()
        .default(true)
        .describe('Whether to preserve the order of inputs in the merged result'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
});
// Result schema
exports.nativeMergeResultSchema = zod_1.z.object({
    mergedData: zod_1.z.any().describe('The merged result based on the operation type'),
    metadata: zod_1.z.object({
        operation: mergeOperationSchema.describe('The merge operation that was performed'),
        inputCount: zod_1.z.number().describe('Number of inputs that were merged'),
        totalItemsProcessed: zod_1.z.number().describe('Total number of items processed across all inputs'),
        totalItemsInResult: zod_1.z.number().describe('Number of items in the final merged result'),
        duplicatesRemoved: zod_1.z
            .number()
            .optional()
            .describe('Number of duplicates removed (for union operations)'),
        conflictsResolved: zod_1.z
            .number()
            .optional()
            .describe('Number of conflicts resolved (for object merges)'),
        executedAt: zod_1.z.string().datetime().describe('When the merge was executed'),
    }),
});
exports.nativeMerge = (0, common_1.createEmitModeToolMetadata)({
    id: 'nativeMerge',
    displayName: 'Merge Data',
    displayDescription: 'Merge multiple arrays, objects, or CSV data with flexible strategies. Supports array concatenation with deduplication, object merging with conflict resolution, and CSV data alignment. Perfect for combining data from multiple sources, consolidating API results, and merging structured datasets.',
    category: 'native',
    color: common_1.BRAND_COLORS.NATIVE,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.NATIVE),
    icon: 'merge',
    argsSchema: exports.nativeMergeArgsSchema,
}, exports.nativeMergeResultSchema);
